import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle, FiArrowLeft, FiChevronDown, FiChevronUp, FiList, FiSave, FiSettings, FiZap } = FiIcons;
const VALID_SCOPES = ['client_facing', 'bpas_internal_team'];

const PlaylistForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const appContextId = !isEdit ? searchParams.get('appId') : '';
  const requestedScope = searchParams.get('scope');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apps, setApps] = useState([]);
  const [contextApp, setContextApp] = useState(null);
  const [smartSource, setSmartSource] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(!appContextId);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visibility_scope: 'bpas_internal_team',
    workflow_status: 'draft',
    app_ids: [],
    tags: [],
    sort_order: 0
  });

  useEffect(() => {
    loadApps();
    if (isEdit) {
      loadPlaylist();
    } else if (appContextId) {
      loadSmartDefaults(appContextId);
    }
  }, [id, appContextId]);

  const loadApps = async () => {
    try {
      const result = await apiCall(() => pb.collection('apps').getFullList({ sort: 'app_name', filter: 'is_active = true' }));
      setApps(result);
    } catch (loadError) {
      console.error('Error loading apps:', loadError);
    }
  };

  const loadSmartDefaults = async (appId) => {
    const safeId = String(appId || '').replace(/[^A-Za-z0-9]/g, '');
    if (!safeId) {
      setError('The selected App context is invalid.');
      return;
    }

    try {
      setLoading(true);
      const filter = `app_ids ?= "${safeId}"`;
      const [appRecord, latestPlaylistResult, latestVideoResult, sortResult] = await Promise.all([
        apiCall(() => pb.collection('apps').getOne(safeId)),
        apiCall(() => pb.collection('playlists').getList(1, 1, { filter, sort: '-updated' })),
        apiCall(() => pb.collection('videos').getList(1, 1, { filter, sort: '-updated' })),
        apiCall(() => pb.collection('playlists').getList(1, 1, { filter, sort: '-sort_order,-updated' }))
      ]);

      const latestPlaylist = latestPlaylistResult.items?.[0] || null;
      const latestVideo = latestVideoResult.items?.[0] || null;
      const validRequestedScope = VALID_SCOPES.includes(requestedScope) ? requestedScope : '';
      const scope = validRequestedScope || latestPlaylist?.visibility_scope || latestVideo?.visibility_scope || 'bpas_internal_team';
      const currentMaxSort = Number(sortResult.items?.[0]?.sort_order || 0);

      setContextApp(appRecord);
      setFormData(prev => ({
        ...prev,
        visibility_scope: scope,
        workflow_status: 'draft',
        app_ids: [safeId],
        sort_order: currentMaxSort + 1
      }));
      setSmartSource(latestPlaylist ? `Reused from “${latestPlaylist.name || 'latest Topic'}”` : latestVideo ? 'Matched to the latest video scope' : 'New App defaults applied');
    } catch (loadError) {
      console.error('Failed to prepare App Topic defaults:', loadError);
      setError('Could not prepare the App defaults. You can still create the Topic manually.');
      setAdvancedOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const loadPlaylist = async () => {
    try {
      setLoading(true);
      const playlist = await apiCall(() => pb.collection('playlists').getOne(id));
      setFormData({
        name: playlist.name || '',
        description: playlist.description || '',
        visibility_scope: playlist.visibility_scope || 'bpas_internal_team',
        workflow_status: playlist.workflow_status || 'draft',
        app_ids: playlist.app_ids || [],
        tags: Array.isArray(playlist.tags) ? playlist.tags : [],
        sort_order: playlist.sort_order ?? 0
      });
    } catch (loadError) {
      console.error('Failed to load Topic data:', loadError);
      setError('Failed to load Topic data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleTagsChange = (event) => {
    const tagsArray = event.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, tags: tagsArray }));
  };

  const handleMultiSelect = (event, field) => {
    const options = Array.from(event.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, [field]: options }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Topic name is required';
    if (formData.visibility_scope === 'client_facing' && (!Array.isArray(formData.app_ids) || formData.app_ids.length === 0)) {
      errors.app_ids = 'Client-facing Topics must be linked to at least one App';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);

    try {
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        visibility_scope: formData.visibility_scope,
        workflow_status: formData.workflow_status,
        app_ids: Array.isArray(formData.app_ids) ? formData.app_ids : [],
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        sort_order: Number.parseInt(formData.sort_order, 10) || 0
      };

      if (isEdit) {
        await apiCall(() => pb.collection('playlists').update(id, dataToSave));
      } else {
        await apiCall(() => pb.collection('playlists').create(dataToSave));
      }
      navigate(appContextId ? `/apps/${appContextId}/setup` : '/playlists');
    } catch (saveError) {
      const pocketBaseErrors = saveError?.data?.data || saveError?.response?.data || {};
      const fieldErrors = {};
      Object.entries(pocketBaseErrors).forEach(([field, fieldError]) => {
        fieldErrors[field] = fieldError?.message || (Array.isArray(fieldError) ? fieldError[0] : fieldError);
      });
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(fieldErrors);
        setError('Could not save Topic. Please fix the highlighted fields.');
      } else {
        setError(saveError?.message || 'Failed to save Topic');
      }
      setAdvancedOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-500';
  const labelClass = 'block text-sm font-medium text-slate-300 mb-2';
  const sectionClass = 'bg-slate-900 rounded-xl p-6 border border-slate-800 mb-6';
  const backTarget = appContextId ? `/apps/${appContextId}/setup` : '/playlists';

  return (
    <div className="max-w-3xl mx-auto pb-12">
      <div className="flex items-center justify-between mb-8 gap-4">
        <div className="flex items-center space-x-4 min-w-0">
          <button type="button" onClick={() => navigate(backTarget)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white">{isEdit ? 'Edit Topic' : 'Create Topic'}</h1>
            {contextApp && <p className="text-sm text-slate-500 mt-1 truncate">For {contextApp.app_name}</p>}
          </div>
        </div>
        <button type="button" onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-lg flex items-center space-x-2 font-medium disabled:opacity-50 shadow-lg shadow-blue-900/20">
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          <span>{loading ? 'Saving...' : 'Save Topic'}</span>
        </button>
      </div>

      {appContextId && contextApp && (
        <div className="mb-6 bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
          <SafeIcon icon={FiZap} className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-200">App targeting applied automatically</p>
            <p className="text-sm text-slate-500 mt-1">{contextApp.app_name} is preselected, scope and sort order are prepared automatically. {smartSource && <span>{smartSource}.</span>}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-200 px-4 py-3 rounded-xl flex items-center mb-6">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={sectionClass}>
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400"><SafeIcon icon={FiList} className="w-4 h-4" /></div>
            <div>
              <h2 className="text-lg font-bold text-white">Topic essentials</h2>
              <p className="text-sm text-slate-500 mt-1">A Topic groups related tutorials. The order can also act as a guided learning sequence.</p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className={labelClass}>Topic Name *</label>
              <input name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="e.g. Weekly Invoicing" />
              {formErrors.name && <p className="text-red-400 text-sm mt-1">{formErrors.name}</p>}
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows="3" className={inputClass} placeholder="What tutorials belong in this Topic and what will the user learn?" />
            </div>
            <div>
              <label className={labelClass}>Status *</label>
              <select name="workflow_status" value={formData.workflow_status} onChange={handleChange} className={inputClass}>
                <option value="draft">Draft</option>
                <option value="review">Review</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>

        <button type="button" onClick={() => setAdvancedOpen(current => !current)} className="w-full mb-6 px-5 py-4 rounded-xl border border-slate-800 bg-slate-900 hover:bg-slate-800/70 flex items-center justify-between text-left">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-800 text-blue-400"><SafeIcon icon={FiSettings} className="w-4 h-4" /></div>
            <div><p className="text-sm font-semibold text-white">Advanced options</p><p className="text-xs text-slate-500 mt-0.5">App targeting, scope, tags and sort order.</p></div>
          </div>
          <SafeIcon icon={advancedOpen ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-slate-500" />
        </button>

        {advancedOpen && (
          <div className={sectionClass}>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className={labelClass}>Visibility Scope *</label>
                <select name="visibility_scope" value={formData.visibility_scope} onChange={handleChange} className={inputClass}>
                  <option value="bpas_internal_team">Internal Team</option>
                  <option value="client_facing">Client Facing</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Apps {formData.visibility_scope === 'client_facing' ? '*' : ''}</label>
                <select multiple name="app_ids" value={formData.app_ids} onChange={(event) => handleMultiSelect(event, 'app_ids')} className={`${inputClass} h-28`}>
                  {apps.map(app => <option key={app.id} value={app.id}>{app.app_name}</option>)}
                </select>
                {formErrors.app_ids && <p className="text-red-400 text-sm mt-1">{formErrors.app_ids}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Tags</label>
                  <input type="text" value={formData.tags.join(', ')} onChange={handleTagsChange} className={inputClass} placeholder="invoicing, payments" />
                </div>
                <div>
                  <label className={labelClass}>Sort Order</label>
                  <input type="number" name="sort_order" value={formData.sort_order} onChange={handleChange} className={inputClass} />
                  {appContextId && <p className="text-xs text-slate-500 mt-1">Calculated automatically for this App.</p>}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default PlaylistForm;